import { ChunkData, ChunkDataProvider, ChunkedMapData, SceneId } from '@common/api';
import { getChunkKey } from '@common/chunkUtils';

/**
 * Client-specific chunk data storage.
 *
 * Implements ChunkDataProvider for use with CollisionSystem.
 */
export class ClientChunkDataService implements ChunkDataProvider {
  /**
   * Per-scene chunk storage: sceneId -> (chunkKey -> ChunkData)
   */
  public chunks: Partial<Record<SceneId, Map<string, ChunkData>>> = {};

  /**
   * Per-scene chunked metadata.
   */
  public sceneMetadata: Partial<Record<SceneId, ChunkedMapData>> = {};

  /**
   * Get all scene IDs that have metadata loaded.
   */
  public getAllSceneIds(): SceneId[] {
    return Object.keys(this.sceneMetadata) as SceneId[];
  }

  /**
   * Get chunks for a scene.
   * Implements ChunkDataProvider interface.
   */
  public getChunks(sceneId: SceneId): Map<string, ChunkData> | undefined {
    return this.chunks[sceneId];
  }

  /**
   * Get metadata for a scene.
   * Implements ChunkDataProvider interface.
   */
  public getSceneMetadata(sceneId: SceneId): ChunkedMapData | undefined {
    return this.sceneMetadata[sceneId];
  }

  /**
   * Reset the service (for testing or reconnection).
   */
  public reset(): void {
    this.chunks = {};
    this.sceneMetadata = {};
  }

  public handleChunkLoad(sceneId: SceneId, chunkX: number, chunkY: number, data: ChunkData): void {
    if (!this.chunks[sceneId]) {
      this.chunks[sceneId] = new Map();
    }
    const chunkKey = getChunkKey(chunkX, chunkY);
    this.chunks[sceneId]!.set(chunkKey, data);
  }

  public handleChunkUnload(sceneId: SceneId, chunkX: number, chunkY: number): void {
    if (this.chunks[sceneId]) {
      const chunkKey = getChunkKey(chunkX, chunkY);
      this.chunks[sceneId]!.delete(chunkKey);
    }
  }
}
